import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { EditIcon, TrashIcon } from "lucide-react";

import { AnalysisAPI } from "@/api";
import {
  DeleteTimelineEventModal,
  EditTimelineEventModal,
  TimelineEvent,
  TimelineEventFormDefaultData,
} from "@/Features";
import { FlyoutTabs, TabPanel } from "./FlyoutTabs";
import {
  FlyoutActivityTab,
  FlyoutDetailsTab,
  FlyoutNotesTab,
  FlyoutTraceIndicatorsTab,
} from "./components";

const tabDefs = [
  {
    label: "Details",
    value: "details",
  },
  {
    label: "Notes",
    value: "notes",
  },
  {
    label: "Indicators",
    value: "trace-indicators",
  },
  {
    label: "Activity",
    value: "activity",
  },
];

export const TimelineEventFlyout: React.FC<{
  open?: boolean;
  onClose?: () => void;
  defaultEventData?: Partial<TimelineEvent>;
  onEdit?: (data: TimelineEventFormDefaultData) => void;
  onDelete?: (data: TimelineEventFormDefaultData) => void;
}> = ({ open = false, onClose, defaultEventData, onEdit, onDelete }) => {
  const theme = useTheme();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["timeline-events", "list", { uuid: defaultEventData?.uuid }],
    queryFn: ({ queryKey }) => {
      const [, , query] = queryKey;
      return AnalysisAPI.TimelineEvents.get(query);
    },
    enabled: !!defaultEventData?.uuid,
    initialData: { data: [defaultEventData] },
  });

  const eventData = data?.data?.[0] || {};

  return (
    <Flyout open={open} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{eventData.event_name}</FlyoutTitle>
            <EditTimelineEventModal
              show={showEditModal}
              defaultFormData={{
                uuid: eventData?.uuid,
                event_name: eventData?.event_name,
                timestamp: eventData?.timestamp,
                event_type_id: eventData?.event_type?.event_type_id,
                artifact_id: eventData?.artifact?.artifact_id,
                account_id: eventData?.account?.account_id,
                description: eventData.description,
                case_id: eventData?.case?.case_id,
                case_uuid: eventData?.case?.uuid,
                source_evidence_uuid: eventData?.source_evidence?.uuid,
                target_evidence_uuid: eventData?.target_evidence?.uuid,
                mitre_tactic_uuid: eventData?.mitre_tactic?.uuid,
                mitre_technique_uuid: eventData?.mitre_technique?.uuid,
              }}
              onClose={() => setShowEditModal(false)}
              onCancel={() => setShowEditModal(false)}
              onSubmit={(data) => {
                refetch();
                onEdit?.(data);
                setShowEditModal(false);
              }}
            />
            <DeleteTimelineEventModal
              show={showDeleteModal}
              defaultFormData={{
                uuid: eventData?.uuid,
                event_name: eventData?.event_name,
                timestamp: eventData?.timestamp,
                event_type_id: eventData?.event_type?.event_type_id,
                artifact_id: eventData?.artifact?.artifact_id,
                account_id: eventData?.account?.account_id,
                description: eventData.description,
                case_id: eventData?.case?.case_id,
                case_uuid: eventData?.case?.uuid,
              }}
              onClose={() => setShowDeleteModal(false)}
              onCancel={() => setShowDeleteModal(false)}
              onSubmit={(data) => {
                onDelete?.(data);
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: 5,
              }}
            >
              <div>Timeline Event</div>
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </div>
          </FlyoutHeader>
          <FlyoutTabs tabDefs={tabDefs}>
            <TabPanel value="details">
              <FlyoutDetailsTab defaultEventData={eventData} />
            </TabPanel>
            <TabPanel value="notes">
              <FlyoutNotesTab defaultEventData={eventData} />
            </TabPanel>
            <TabPanel value="trace-indicators">
              <FlyoutTraceIndicatorsTab defaultEventData={eventData} />
            </TabPanel>
            <TabPanel value="activity">
              <FlyoutActivityTab defaultData={eventData} />
            </TabPanel>
          </FlyoutTabs>
        </>
      )}
    </Flyout>
  );
};
